export type ProjectTakhmeenCreateFormData = {
  projectId?: string;
  itsdataId?: string;
  units?: string | number;
  committedAmount?: string | number;
  remarks?: string | null;
  /** Ignored on write — server derives override from units × project unit amount. */
  isAmountOverridden?: boolean;
};

export type ProjectTakhmeenUpdateFormData = {
  units?: string | number;
  committedAmount?: string | number;
  remarks?: string | null;
  isAmountOverridden?: boolean;
};

export type ProjectTakhmeenPreviousData = {
  units?: number;
  committedAmount?: number;
  paidAmount?: number;
};

export type ProjectTakhmeenCreatePayload = {
  projectId: string;
  itsdataId: string;
  units: number;
  committedAmount: number;
  remarks?: string;
};

export type ProjectTakhmeenUpdatePayload = {
  units?: number;
  committedAmount?: number;
  remarks?: string;
};

function normalizeRemarks(raw: unknown): string | undefined {
  if (raw == null) return undefined;
  if (typeof raw !== "string") return String(raw).trim() || undefined;
  const trimmed = raw.trim();
  return trimmed || undefined;
}

/** Coerce money/units to a non-negative integer (API `@IsInt()`). */
export function parseProjectTakhmeenIntAmount(
  raw: unknown,
  { fieldLabel = "Amount", required = true }: { fieldLabel?: string; required?: boolean } = {}
): number {
  if (raw === "" || raw === null || raw === undefined) {
    if (!required) return NaN;
    throw new Error(`${fieldLabel} is required`);
  }
  const n = Number(raw);
  if (!Number.isFinite(n)) {
    throw new Error(`${fieldLabel} must be a valid number`);
  }
  return Math.round(n);
}

export function parseProjectTakhmeenUnits(raw: unknown): number {
  if (raw === "" || raw === null || raw === undefined) {
    throw new Error("Units is required");
  }
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0) {
    throw new Error("Units must be 0 or more");
  }
  return Math.round(n);
}

export function validateProjectTakhmeenUnitsField(value: unknown): string | undefined {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return "Must be 0 or more";
}

export function validateProjectTakhmeenCommittedAmountField(value: unknown): string | undefined {
  if (value === "" || value === null || value === undefined) return "Required";
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return "Must be 0 or more";
}

/** Edit form: committed amount must not drop below amount already received. */
export function validateProjectTakhmeenCommittedNotBelowPaid(paidAmount: number | undefined) {
  return (value: unknown): string | undefined => {
    if (paidAmount == null || paidAmount <= 0) return undefined;
    const n = Number(value);
    if (!Number.isFinite(n)) return undefined;
    if (Math.round(n) < paidAmount) {
      return `Committed amount cannot be less than paid amount (${paidAmount})`;
    }
  };
}

export function transformProjectTakhmeenCreate(
  data: ProjectTakhmeenCreateFormData,
  options?: { routeProjectId?: string | null }
): ProjectTakhmeenCreatePayload {
  const projectId = (options?.routeProjectId ?? data.projectId)?.toString().trim();
  if (!projectId) {
    throw new Error("Project is required");
  }

  const itsdataId = typeof data.itsdataId === "string" ? data.itsdataId.trim() : data.itsdataId;
  if (!itsdataId || typeof itsdataId !== "string") {
    throw new Error("ITS is required");
  }

  const units = parseProjectTakhmeenUnits(data.units);
  const committedAmount = parseProjectTakhmeenIntAmount(data.committedAmount, {
    fieldLabel: "Committed amount",
  });
  if (committedAmount < 0) {
    throw new Error("Committed amount must be 0 or more");
  }

  return {
    projectId,
    itsdataId,
    units,
    committedAmount,
    remarks: normalizeRemarks(data.remarks),
  };
}

/**
 * Build update payload. Omits `units` / `committedAmount` when unchanged vs the loaded
 * record so remarks-only edits succeed after receipts exist.
 */
export function transformProjectTakhmeenUpdate(
  data: ProjectTakhmeenUpdateFormData,
  options: { previousData: ProjectTakhmeenPreviousData }
): ProjectTakhmeenUpdatePayload {
  const { previousData } = options;
  const out: ProjectTakhmeenUpdatePayload = {};

  const units = parseProjectTakhmeenUnits(data.units);
  const unitsChanged = units !== previousData.units;
  if (unitsChanged) {
    out.units = units;
  }

  const committedAmount = parseProjectTakhmeenIntAmount(data.committedAmount, {
    fieldLabel: "Committed amount",
  });
  if (committedAmount < 0) {
    throw new Error("Committed amount must be 0 or more");
  }
  const committedChanged = committedAmount !== previousData.committedAmount;
  // When units change, always send committedAmount so the API does not recalculate
  // from units × unit amount and drop a custom pledge total.
  if (committedChanged || unitsChanged) {
    out.committedAmount = committedAmount;
  }

  out.remarks = normalizeRemarks(data.remarks);

  return out;
}
